import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { submitForm } from '../../api/forms.api';
import { useMessage } from '../../hooks/useMessage';
import { FormsProps, QuizQuestion } from '../../types/forms';

const FEEDBACK_DELAY_MS = 1400;

type FilterMode = 'all' | 'correct' | 'incorrect';

const QuizForm = ({ formId, quizQuestions, skin }: FormsProps) => {
  const { showMessage, MessageContainer } = useMessage();
  const questions = useMemo(() => (quizQuestions ?? []) as QuizQuestion[], [quizQuestions]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [filter, setFilter] = useState<FilterMode>('all');
  const current = questions[currentIndex];
  const isLastQuestion = currentIndex === Math.max(questions.length - 1, 0);
  const progress =
    showFeedback && isLastQuestion ? 100 : ((currentIndex + 1) / Math.max(questions.length, 1)) * 100;
  const score = useMemo(() => {
    return questions.reduce((acc, q) => acc + (answers[q.id] === q.correctAnswer ? 1 : 0), 0);
  }, [questions, answers]);
  const filteredQuestions = useMemo(() => {
    if (filter === 'all') return questions;

    return questions.filter((q) => {
      const isCorrect = answers[q.id] === q.correctAnswer;
      return filter === 'correct' ? isCorrect : !isCorrect;
    });
  }, [questions, answers, filter]);

  if (!formId) return null;
  if (!questions.length) {
    return (
      <Box p={2}>
        <Typography variant="h6">Quiz is empty (no questions from API).</Typography>
        <MessageContainer />
      </Box>
    );
  }

  const submitQuiz = async (finalAnswers: Record<string, string>) => {
    const payload = {
      form_id: +formId,
      data: Object.keys(finalAnswers).map((id) => ({
        id,
        value: finalAnswers[id],
      })),
    };
    setSubmitting(true);
    try {
      await submitForm(payload);
      showMessage('Quiz submitted successfully!', 'success');
    } catch {
      showMessage('Could not submit quiz. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const onChoose = (option: string) => {
    if (selected || submitting) return;
    setSelected(option);
    setShowFeedback(true);
    const nextAnswers = { ...answers, [current.id]: option };
    setAnswers(nextAnswers);
    window.setTimeout(() => {
      if (!isLastQuestion) {
        setSelected(null);
        setShowFeedback(false);
        setCurrentIndex((i) => i + 1);
      } else {
        setShowResults(true);
        void submitQuiz(nextAnswers);
      }
    }, FEEDBACK_DELAY_MS);
  };

  const optionSx = (option: string) => {
  if (!showFeedback) return {};

  const isSelected = option === selected;
  if (!isSelected) return {}; 

  const isAnswerCorrect = selected === current.correctAnswer;

  if (isAnswerCorrect) {
    return {
      borderColor: '#008000',
      backgroundColor: 'rgba(0,128,0,0.5)',
      color: '#fff',
      fontWeight: 700,
    };
  }

  return {
    borderColor: '#c40303',
    backgroundColor: 'rgba(196,3,3,0.5)',
    color: '#fff',
    fontWeight: 700,
  };
};

  const resultsOptionSx = (q: QuizQuestion, opt: string) => {
  const picked = answers[q.id];
  const isPicked = opt === picked;
  const isAnswerCorrect = picked === q.correctAnswer;
  const neutral = {
    backgroundColor: '#f2f2f2',
    borderColor: '#e0e0e0',
    color: '#000',
    fontWeight: 600,
  };

  if (!isPicked) return neutral;
  if (isAnswerCorrect) {
    return {
      backgroundColor: '#008000',
      borderColor: '#008000',
      color: '#fff',
      fontWeight: 700,
    };
  }

  return {
    backgroundColor: '#c40303',
    borderColor: '#c40303',
    color: '#fff',
    fontWeight: 700,
  };
};

  if (showResults) {
    return (
      <Box sx={{ maxWidth: 760, margin: '32px auto' }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 400 }}>
            You answered correctly <b>{score}</b> out of <b>{questions.length}</b> questions.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select value={filter} onChange={(e) => setFilter(e.target.value as FilterMode)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="correct">Correct</MenuItem>
              <MenuItem value="incorrect">Incorrect</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Stack spacing={2}>
          {filteredQuestions.map((q) => (
            <Paper key={q.id} sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Question {questions.findIndex((x) => x.id === q.id) + 1}
              </Typography>
              <Typography sx={{ mb: 2 }}>{q.question}</Typography>
              <Stack spacing={1.2}>
                {q.options.map((opt) => (
                  <Button
                    key={opt}
                    variant="outlined"
                    disabled
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      borderRadius: 1.5,
                      p: 1.4,
                      '&.Mui-disabled': {
                        opacity: 1,
                        color: '#000',
                        WebkitTextFillColor: 'inherit',
                      },
                      ...resultsOptionSx(q, opt),
                    }}
                  >
                    {opt}
                  </Button>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
        {submitting && (
          <Typography sx={{ mt: 2, opacity: 0.7, textAlign: 'center' }}>Submitting...</Typography>
        )}
        <MessageContainer />
      </Box>
    );
  }
  return (
    <Box sx={{ maxWidth: 700, margin: '32px auto' }}>
      {skin === 'skin1' && (
        <Box mb={2}>
          <Typography variant="h4" gutterBottom>
            Quiz
          </Typography>
          <Typography sx={{ opacity: 0.7 }}>Select an answer — you’ll see feedback immediately.</Typography>
        </Box>
      )}

      <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography sx={{ opacity: 0.7, mb: 1 }}>
          Question {currentIndex + 1} of {questions.length}
        </Typography>

        <Typography variant="h6" sx={{ mb: 2 }}>
          {current.question}
        </Typography>

        <Stack spacing={1.5}>
          {current.options.map((opt) => (
            <Button
              key={opt}
              variant="outlined"
              disabled={!!selected || submitting}
              onClick={() => onChoose(opt)}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                borderRadius: 2,
                p: 1.5,
                transition: 'all 0.25s ease',
                '&.Mui-disabled': {
                  opacity: 1,
                  color: '#000',
                  WebkitTextFillColor: 'inherit',
                },
                ...optionSx(opt),
              }}
            >
              {opt}
            </Button>
          ))}
        </Stack>
        {submitting && <Typography sx={{ mt: 2, opacity: 0.7 }}>Submitting...</Typography>}
      </Paper>
      <MessageContainer />
    </Box>
  );
};

export default QuizForm;